import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FileText,
  Kanban,
  Network,
  FolderOpen,
  Calendar,
  MessageSquare,
  Settings,
  User,
  Home,
  LogOut,
  Brain,
  CheckSquare,
  ClipboardList,
  GraduationCap,
  Crown,
  Infinity
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../hooks/useTheme';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { logout, user } = useAuth();
  const { colors } = useTheme();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Notes', href: '/notes', icon: FileText },
    { name: 'Projects', href: '/projects', icon: Kanban },
    { name: 'Graph View', href: '/graph', icon: Network },
    { name: 'Documents', href: '/documents', icon: FolderOpen },
    { name: 'Calendar', href: '/calendar', icon: Calendar },
    { name: 'AI Chat', href: '/chat', icon: MessageSquare },
  ];

  const academicNavigation = [
    { name: 'Calendario Académico', href: '/academic-calendar', icon: GraduationCap },
    { name: 'Tablero de Tareas', href: '/task-board', icon: ClipboardList },
    { name: 'Lista Simple', href: '/simple-todo', icon: CheckSquare },
  ];

  const bottomNavigation = [
    { name: 'Subscription', href: '/subscription', icon: Crown },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const isPremium = user?.subscription.plan === 'premium';
  const isUnlimited = user?.subscription.plan === 'unlimited';
  const isPaid = isPremium || isUnlimited;

  const getPlanIcon = () => {
    if (isUnlimited) return <Infinity className="w-3 h-3" style={{ color: colors.yellow }} />;
    if (isPremium) return <Crown className="w-3 h-3" style={{ color: colors.blue }} />;
    return null;
  };

  const getPlanName = () => {
    if (isUnlimited) return 'Unlimited';
    if (isPremium) return 'Premium';
    return 'Free';
  };

  const getPlanColor = () => {
    if (isUnlimited) return colors.yellow;
    if (isPremium) return colors.blue;
    return colors.green;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`h-full w-64 lg:relative lg:block ${
          // En móvil: fixed y transformación
          'fixed left-0 top-0 z-50 transform transition-transform duration-300 ease-in-out lg:transform-none ' +
          (isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0')
        }`}
        style={{ 
          backgroundColor: colors.mantle,
          borderRight: `1px solid ${colors.surface0}`
        }}
      >
        {/* Resto del contenido del sidebar */}
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center px-6 py-4 border-b" style={{ borderColor: colors.surface0 }}>
            <Brain className="w-8 h-8 mr-3" style={{ color: colors.blue }} />
            <div className="flex-1">
              <h1 className="text-xl font-bold" style={{ color: colors.text }}>
                Enki AI
              </h1>
              {isPaid && (
                <div className="flex items-center gap-1 mt-1">
                  {getPlanIcon()}
                  <span className="text-xs" style={{ color: getPlanColor() }}>
                    {getPlanName()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
            {/* Main Navigation */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.subtext0 }}>
                Principal
              </h3>
              <div className="space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={onClose}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        isActive 
                          ? 'text-white' 
                          : 'hover:opacity-80'
                      }`}
                      style={{
                        backgroundColor: isActive ? colors.blue : 'transparent',
                        color: isActive ? 'white' : colors.text
                      }}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            </div>

            {/* Academic Navigation */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.subtext0 }}>
                Académico
              </h3>
              <div className="space-y-2">
                {academicNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={onClose}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        isActive 
                          ? 'text-white' 
                          : 'hover:opacity-80'
                      }`}
                      style={{
                        backgroundColor: isActive ? colors.green : 'transparent',
                        color: isActive ? 'white' : colors.text
                      }}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Usage Stats for Non-Unlimited Users */}
          {!isUnlimited && user && (
            <div className="px-4 py-3 border-t" style={{ borderColor: colors.surface0 }}>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs" style={{ color: colors.subtext0 }}>
                      Almacenamiento
                    </span>
                    <span className="text-xs" style={{ color: colors.subtext0 }}>
                      {user.subscription.usage.storageUsed.toFixed(0)}/{user.subscription.plan === 'free' ? '500 MB' : '5 GB'}
                    </span>
                  </div>
                  <div 
                    className="w-full h-1 rounded-full"
                    style={{ backgroundColor: colors.surface0 }}
                  >
                    <div 
                      className="h-1 rounded-full transition-all"
                      style={{ 
                        backgroundColor: colors.blue,
                        width: `${Math.min((user.subscription.usage.storageUsed / (user.subscription.plan === 'free' ? 500 : 5120)) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs" style={{ color: colors.subtext0 }}>
                      IA hoy
                    </span>
                    <span className="text-xs" style={{ color: colors.subtext0 }}>
                      {user.subscription.plan === 'free' 
                        ? `${user.subscription.usage.aiQueriesUsed}/10`
                        : 'Ilimitadas'
                      }
                    </span>
                  </div>
                  <div 
                    className="w-full h-1 rounded-full"
                    style={{ backgroundColor: colors.surface0 }}
                  >
                    <div 
                      className="h-1 rounded-full transition-all"
                      style={{ 
                        backgroundColor: colors.green,
                        width: user.subscription.plan === 'free' 
                          ? `${(user.subscription.usage.aiQueriesUsed / 10) * 100}%`
                          : '100%'
                      }}
                    />
                  </div>
                </div>

                {isPremium && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs" style={{ color: colors.subtext0 }}>
                        IA Premium
                      </span>
                      <span className="text-xs" style={{ color: colors.subtext0 }}>
                        {user.subscription.usage.premiumQueriesUsed}/20
                      </span>
                    </div>
                    <div 
                      className="w-full h-1 rounded-full"
                      style={{ backgroundColor: colors.surface0 }}
                    >
                      <div 
                        className="h-1 rounded-full transition-all"
                        style={{ 
                          backgroundColor: colors.yellow,
                          width: `${(user.subscription.usage.premiumQueriesUsed / 20) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bottom navigation */}
          <div className="px-4 py-4 border-t space-y-2" style={{ borderColor: colors.surface0 }}>
            {bottomNavigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive 
                      ? 'text-white' 
                      : 'hover:opacity-80'
                  }`}
                  style={{
                    backgroundColor: isActive ? colors.blue : 'transparent',
                    color: isActive ? 'white' : colors.text
                  }}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                  {item.name === 'Subscription' && isPaid && (
                    <div className="ml-auto">
                      {getPlanIcon()}
                    </div>
                  )}
                </NavLink>
              );
            })}
            
            <button
              onClick={logout}
              className="flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 hover:opacity-80"
              style={{ color: colors.red }}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;